import { type ParsedUrlQuery } from 'node:querystring';

import {
  COMPARISON_ACTIVE_PANEL_URL_VALUES,
  COMPARISON_ACTIVE_PANELS,
  COMPARISON_MODES,
  DEFAULT_COMPARISON_ACTIVE_PANEL,
  DEFAULT_COMPARISON_LINKED,
  DEFAULT_COMPARISON_MODE,
  DEFAULT_COMPARISON_SLIDER_POSITION,
  MAX_COMPARISON_SLIDER_POSITION,
  MIN_COMPARISON_SLIDER_POSITION,
  type PrivateAssetComparisonURLState,
} from '~/constants/assetComparison';
import { QueryParamNames } from '~/constants/search';
import { parseComparisonClipId } from '~/utils/parseComparisonClipId';

const getFirstQueryValue = (value: ParsedUrlQuery[string]) => (Array.isArray(value) ? value[0] : value);

const parseLinked = (value: string | undefined) => {
  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return DEFAULT_COMPARISON_LINKED;
};

const parseSliderPosition = (value: string | undefined) => {
  if (!value) {
    return DEFAULT_COMPARISON_SLIDER_POSITION;
  }

  const position = Number(value);

  if (
    !Number.isFinite(position) ||
    position < MIN_COMPARISON_SLIDER_POSITION ||
    position > MAX_COMPARISON_SLIDER_POSITION
  ) {
    return DEFAULT_COMPARISON_SLIDER_POSITION;
  }

  return position;
};

/** Parses and independently validates every URL field owned by private asset comparison. */
export const parsePrivateAssetComparisonURLState = (query: ParsedUrlQuery): PrivateAssetComparisonURLState => {
  const activePanelValue = getFirstQueryValue(query[QueryParamNames.comparisonActivePanel]);
  const modeValue = getFirstQueryValue(query[QueryParamNames.comparisonMode]);
  const activePanel = COMPARISON_ACTIVE_PANELS.find(
    (panel) => COMPARISON_ACTIVE_PANEL_URL_VALUES[panel] === activePanelValue,
  );

  return {
    activePanel: activePanel ?? DEFAULT_COMPARISON_ACTIVE_PANEL,
    comparisonClipId: parseComparisonClipId(query),
    isLinked: parseLinked(getFirstQueryValue(query[QueryParamNames.comparisonLinked])),
    mode: COMPARISON_MODES.find((mode) => mode === modeValue) ?? DEFAULT_COMPARISON_MODE,
    sliderPosition: parseSliderPosition(getFirstQueryValue(query[QueryParamNames.comparisonSliderPosition])),
  };
};
